import { useState, useEffect, useCallback, useRef } from 'react';
import { useCashback } from '@/services/cashback.service';

type CashbackHistoryTabProps = {
    closeModal: () => void;
};

type AccrualItem = {
    id: string;
    periodMonth: number;
    periodYear: number;
    periodLabel: string;
    status: string;
    tierApplied: string;
    totalSpend: number;
    cashbackEarned: number;
    amountRedeemed: number;
    transactionCount: number;
    vestedAt: string | null;
};

export const CashbackHistoryTab = ({ }: CashbackHistoryTabProps) => {
    const [accruals, setAccruals] = useState<AccrualItem[]>([]);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const { getCashbackAccruals } = useCashback();
    const observerRef = useRef<IntersectionObserver | null>(null);
    const lastElementRef = useRef<HTMLTableRowElement | null>(null);

    // Fetch accruals
    const fetchAccruals = useCallback(async (pageNum: number) => {
        if (isLoading) return;

        setIsLoading(true);
        const { content, succeeded } = await getCashbackAccruals(pageNum, 10);

        if (succeeded && content) {
            setAccruals(prev => pageNum === 1 ? content.items : [...prev, ...content.items]);
            setHasMore(pageNum < content.totalPages);
        }

        setIsLoading(false);
    }, [getCashbackAccruals, isLoading]);

    // Initial load
    useEffect(() => {
        fetchAccruals(1);
    }, []);

    // Infinite scroll
    useEffect(() => {
        if (isLoading) return;

        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prev => prev + 1);
            }
        });

        if (lastElementRef.current) {
            observerRef.current.observe(lastElementRef.current);
        }

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [isLoading, hasMore]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            fetchAccruals(page);
        }
    }, [page]);

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
        }).format(amount).replace('$', '');
    };

    // Format date
    const formatDate = (periodLabel: string) => {
        return periodLabel;
    };

    return (
        <div className='w-full'>
            <div className='overflow-x-auto'>
                <table className='w-full'>
                    <thead>
                        <tr className='border-b dark:border-[#717171] border-[#CECECE]'>
                            <th className='text-left py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Tier & Rate
                            </th>
                            <th className='text-left py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Date
                            </th>
                            <th className='text-right py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Earned
                            </th>
                            <th className='text-right py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Spend
                            </th>
                            <th className='text-right py-[1.2rem] px-[1.6rem] font-medium text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                Fee
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {accruals.map((accrual, index) => {
                            const isLast = index === accruals.length - 1;
                            return (
                                <tr
                                    key={accrual.id}
                                    ref={isLast ? lastElementRef : null}
                                    className='border-b dark:border-[#717171] border-[#CECECE] hover:dark:bg-[#1A1A1A] hover:bg-[#F6F6F6] transition-colors'
                                >
                                    <td className='py-[1.2rem] px-[1.6rem] text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        {accrual.tierApplied}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        {formatDate(accrual.periodLabel)}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-right text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        ${formatCurrency(accrual.cashbackEarned)}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-right text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        ${formatCurrency(accrual.totalSpend)}
                                    </td>
                                    <td className='py-[1.2rem] px-[1.6rem] text-right text-[1.3rem] dark:text-[#DEDEDE] text-[#121212]'>
                                        ${formatCurrency(accrual.amountRedeemed)}
                                    </td>
                                </tr>
                            );
                        })}
                        {isLoading && (
                            <tr>
                                <td colSpan={5} className='py-[2rem] text-center'>
                                    <div className='flex justify-center items-center gap-[1rem]'>
                                        <div className='w-[2rem] h-[2rem] border-[0.2rem] border-[#57E8BA] border-t-transparent rounded-full animate-spin'></div>
                                        <span className='text-[1.3rem] dark:text-[#929292] text-[#717171]'>Loading...</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {accruals.length === 0 && !isLoading && (
                <div className='py-[4rem] text-center'>
                    <p className='text-[1.4rem] dark:text-[#929292] text-[#717171]'>No history found</p>
                </div>
            )}
        </div>
    );
};
